import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { Dropdown } from 'react-bootstrap';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { format, startOfYear } from 'date-fns';

import { UseOutletContextJournal } from './Journal';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { AccountingEntryDA } from '../../../../services/admin/accounting.entry.service';
import { ExchangeRateDA } from '../../../../services/admin/exchange.rate.service';
import { FiscalYearDA } from '../../../../services/admin/fiscal.year.service';
import { ApiResponse } from '../../../../models/api.response.model';
import { BookingJournalDA } from '../../../../services/admin/booking.journal.service';
import { BookingJournal } from '../../../../models/booking.journal.model';
import { Error } from '../../../../models/error.model';
import { Names } from '../../../../models/names.model';
import { CompanyAccountingBookingJournalEntries } from '../../../../models/company.accounting.booking.journal.entries.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { AccountingEntry } from '../../../../models/accounting.entry.model';
import { AccountingAccount } from '../../../../models/accounting.account.model';
import { CustomSelectClassNames, CustomSelectStyles } from '../../../../styles/select.style';
import { formatAccountingAccount, formatMoney } from '../../../../scripts/format.value.script';
import { getError } from '../../../../languages/translations/response';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { accountingAccountSelected, taxFeeSelected } from '../../../../scripts/list.selected.script';
import { isValidObjectAttributes } from '../../../../scripts/compare.object.script';
import { suffixLegend } from '../../../../scripts/name.legend.script';
import { entryTypeLabelKey } from '../../../../scripts/entry.type.label.script';
import { legendInvalid, legendValid, legendValidRequiredRestart, legendValidInvalidRestart } from '../../../../utils/legend.data.util';
import { evaluateLegendListObjectRequired } from '../../../../scripts/validate.legend.script';
import { expressions } from '../../../../libraries/regular.expressions.library';
import { toastError, toastSuccess } from '../../../../utils/toast.util';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { accountingAccountCode, accountingAccountName } from '../../../../scripts/item.account.script';
import { usePaginationUrl } from '../../../../hooks/usePaginationUrl';

import AppAccountingFilterInterval from '../component/AccountingFilterInterval';
import AppPreloader from '../../../../components/Preloader';
import AppLoading from '../../../../components/Loading';
import AppLegend from '../../../../components/element/Legend';
import AppPagination from '../../../../components/element/Pagination';
import AppModalAttachDocument from '../../../../components/modal/ModalAttachDocument';
import AppModalVoucherUploadFile from '../../../../components/modal/ModalVoucherUploadFile';
import { modalShow } from '../../../../utils/modal.util';
import { Voucher } from '../../../../models/voucher.model';
import { MediaDA } from '../../../../services/admin/media.service';
import FileSaver from 'file-saver';
import { attachmentPdfFilename, exportFallbackFilename } from '../../../../scripts/document.filename.script';
import ButtonEntryDocuments from '../../../../components/button/ButtonEntryDocuments';
import DropdownActions from '../../../../components/dropdown/DropdownActions';
import { ReportDA } from '../../../../services/admin/report.service';
import { triggerBlobDownload } from '../../../../scripts/export.blob.script';

const entryTdStyle: React.CSSProperties = { verticalAlign: 'middle', position: 'relative' }
const legendAbsStyle: React.CSSProperties = { position: 'absolute', left: 0, right: 0, zIndex: 1 }

const accountSelectStyles = {
  ...CustomSelectStyles,
  control: (base: any) => ({
    ...CustomSelectStyles.control(base),
    flexWrap: 'nowrap' as const,
  }),
  singleValue: (base: any) => ({
    ...base,
    whiteSpace: 'normal' as const,
    lineHeight: '1.3',
  }),
  menu: (base: any) => ({
    ...CustomSelectStyles.menu(base),
    minWidth: '320px',
  }),
  option: (base: any, state: any) => ({
    ...CustomSelectStyles.option(base, state),
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  }),
};

export interface JournalViewPageProps {};

let errorResponse: Error, accountingEntryResponse: AccountingEntry, bookingJournalResponse: BookingJournal, companyAccountingBookingJournalEntriesResponse: CompanyAccountingBookingJournalEntries;

function entryDocument(entry: any, lang: any): { text: string; to: string | null } {
  const line = entry?.debits?.[0] ?? entry?.credits?.[0]
  const ref = line?.reference
  const type = line?.type

  // Use the stored description as the display text when available
  const storedDescription = entry?.description

  if (!ref || !type) {
    return { text: storedDescription || '', to: null }
  }

  const num = ref.document_number ?? ref.invoice_number ?? ref.provider_invoice_number ?? ref.expense_number ?? ref.sale_number ?? ''

  // Fallback label: only used when no stored description
  const fallbackLabel = lang.labels[entryTypeLabelKey(type, num)] ?? type
  const fallbackText = [fallbackLabel, num].filter(Boolean).join(' ')

  const text = storedDescription || fallbackText

  let to: string | null = null
  switch (type) {
    case 'invoice':
      to = ref.id_invoice ? `/main/sale/invoice/view/${ref.id_invoice}` : null; break
    case 'provider-invoice':
      to = ref.id_provider_invoice ? `/main/purchase/invoice/view/${ref.id_provider_invoice}` : null; break
    case 'expense':
      to = ref.id_expense ? `/main/purchase/expense/view/${ref.id_expense}` : null; break
    case 'payment':
      to = ref.id_invoice ? `/main/sale/invoice/view/${ref.id_invoice}` : (ref.id_provider_invoice ? `/main/purchase/invoice/view/${ref.id_provider_invoice}` : null); break
    case 'reminder':
      to = ref.id_invoice ? `/main/sale/invoice/view/${ref.id_invoice}` : null; break
    default:
      to = entry?.id ? `/main/accounting/entry/view/${entry.id}` : null; break
  }
  return { text, to }
}

const JournalViewPage: React.FunctionComponent<JournalViewPageProps> = props => {
  const {setRoute, companyForUser, settingsForCompany, accountingAccountsForCompany, bookingJournalsForCompany} = UseOutletContextJournal()
  const {lang} = StateLanguage()
  const param = useParams()
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()
  const [sp, setSp] = useSearchParams()

  const urlQ = sp.get('q') ?? ''
  const [searchText, setSearchText] = useState(urlQ)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const setUrlQ = useCallback((q: string) => {
    setSp(prev => {
      const next = new URLSearchParams(prev)
      if (q) { next.set('q', q) } else { next.delete('q') }
      next.delete('page')
      return next
    }, { replace: true })
  }, [setSp])

  const handleSearchChange = (value: string) => {
    setSearchText(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setUrlQ(value), 350)
  }

  const handleSearchClear = () => {
    setSearchText('')
    if (debounceRef.current) clearTimeout(debounceRef.current)
    setUrlQ('')
  }

  useEffect(() => { return () => { if (debounceRef.current) clearTimeout(debounceRef.current) } }, [])

  const [mounted, setMounted] = useState(false)
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [filter, setFilter] = useState({booking_journal: '', options: {accounting_account: '', interval: '', start_date: '', end_date: ''}})
  const [type, setType] = useState('simple')
  const [taxCode, setTaxCode] = useState('')
  const [taxRate, setTaxRate] = useState(0)
  const [taxIncluded, setTaxIncluded] = useState('')
  const [transaction, setTransaction] = useState('')
  const [vatInputType, setVatInputType] = useState('goods')
  const [vouchers, setVouchers] = useState<{list: boolean, value: {name: string, size: number, type: string, view: string, file: any}[], valid: {name: boolean, file: boolean}[]}>({list: false, value: [], valid: []})
  const [pendingAttachments, setPendingAttachments] = useState<{id: string, name: string}[]>([])
  const [entry, setEntry] = useState({value: '', valid: false})
  const [entryDate, setEntryDate] = useState({value: '', valid: false})
  const [entryJournal, setEntryJournal] = useState({value: '', valid: false})
  const [entryRows, setEntryRows] = useState({value: [{debit: {id: '', text: ''}, credit: {id: '', text: ''}, label: '', sum: ''}], valid: [{debit: false, credit: false, label: false, sum: false}]})
  const [treasuryLockedRows, setTreasuryLockedRows] = useState<Set<number>>(new Set())
  const [rowCurrency, setRowCurrency] = useState<{code: string, amount_original: string, exchange_rate: string}[]>([{code: '', amount_original: '', exchange_rate: ''}])
  const [rowTaxCodes, setRowTaxCodes] = useState<string[]>([''])
  const loadedEntryRef = useRef<any>(null)
  const [bookingJournal, setBookingJournal] = useState<BookingJournal | null>(null)
  const userTouchedJournalRef = useRef(false)
  const [accounitngEntries, setAccountingEntries] = useState<AccountingEntry[] | null>(null)
  const [colors, setColors] = useState<{create: AccountingEntry | null, update: AccountingEntry | null}>({create: null, update: null})
  const [options, setOptions] = useState<{journal: {value: string, label: string}[], debit: {value: string, label: string}[], credit: {value: string, label: string}[]}>({journal: [], debit: [], credit: []})
  const [search, setSearch] = useState<{text: string, list: AccountingAccount[]}>({text: '', list: []})
  const [storage, setStorage] = useState<any>(null)
  const [exporting, setExporting] = useState(false)

  const journalsById = useMemo(
    () => new Map((bookingJournalsForCompany ?? []).map(j => [j.id, j])),
    [bookingJournalsForCompany]
  )

  const LAST_JOURNAL_STORAGE_KEY = 'da_last_manual_entry_journal';

  const saveLastJournal = (journalId: string) => {
    if (!companyForUser?.id || !journalId) return;
    try {
      const store = JSON.parse(localStorage.getItem(LAST_JOURNAL_STORAGE_KEY) ?? '{}');
      store[companyForUser.id] = journalId;
      localStorage.setItem(LAST_JOURNAL_STORAGE_KEY, JSON.stringify(store));
    } catch { /* localStorage unavailable */ }
  };

  const resolveDefaultJournal = (): {value: string, valid: boolean} => {
    if (!companyForUser?.id || !bookingJournalsForCompany?.length) return {value: '', valid: false};
    // Try last used journal for this company
    try {
      const store = JSON.parse(localStorage.getItem(LAST_JOURNAL_STORAGE_KEY) ?? '{}');
      const lastId = store[companyForUser.id];
      if (lastId && journalsById.has(lastId)) return {value: lastId, valid: true};
    } catch { /* localStorage unavailable */ }
    // Fallback: Principal journal (type === 'principal')
    const principal = bookingJournalsForCompany.find(j => j.type === 'principal');
    if (principal) return {value: principal.id, valid: true};
    return {value: '', valid: false};
  };

  const normalizeAccountingEntry = (item: any, fallbackJournalId?: string) => ({
    ...item,
    id: item.id ?? item._id ?? '',
    id_booking_journal: item.id_journal ?? item.id_booking_journal ?? fallbackJournalId ?? '',
    created_at: item.created_at ?? item.createdAt ?? '',
    updated_at: item.updated_at ?? item.updatedAt ?? '',
  })

  const resolveJournalName = (id_journal: string): string => {
    if (id_journal === 'all') return lang.labels.bookingJournal
    return journalsById.get(id_journal)?.name ?? 'N/A'
  }

  const loadBookingJournal = async (id_company: string, id_booking_journal: string) => {
    await BookingJournalDA.getBookingJournal(id_company, id_booking_journal).then( (response) => {
      if (response.status === 200) {
        const raw = response.data
        bookingJournalResponse = raw?.booking_journal ?? raw

        setBookingJournal(bookingJournalResponse)
        setEntryJournal({value: bookingJournalResponse.id, valid: true})
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const loadAccountingBookingJournalEntries = async (id_company: string, id_booking_journal: string, page: number = urlPage) => {
    if (companyForUser) {
      await AccountingEntryDA.getAccountingBookingJournalEntries(id_company, id_booking_journal === 'all' ? '' : id_booking_journal, filter.options.accounting_account, filter.options.interval, filter.options.start_date, filter.options.end_date, page, pagination.limit, urlQ).then( (response) => {
        if (response.status === 200) {
          const raw = response.data

          let tempEntries: AccountingEntry[]
          if (raw?.items) {
            tempEntries = raw.items
          } else if (Array.isArray(raw)) {
            tempEntries = raw
          } else if (raw?.accounting_entries) {
            tempEntries = raw.accounting_entries
          } else {
            tempEntries = []
          }

          tempEntries = tempEntries.map((item: any) => normalizeAccountingEntry(item, id_booking_journal))

          const resolvedFilter = raw?.filter ?? {
            accounting_account: filter.options.accounting_account,
            interval: filter.options.interval,
            start_date: filter.options.start_date,
            end_date: filter.options.end_date
          }

          companyAccountingBookingJournalEntriesResponse = {
            id_company: raw?.id_company ?? id_company,
            id_booking_journal: raw?.id_journal ?? raw?.id_booking_journal ?? id_booking_journal,
            filter: resolvedFilter,
            accounting_entries: tempEntries
          }

          localStorage.setItem('filter_booking_journal_entries', JSON.stringify({
            company_id: companyAccountingBookingJournalEntriesResponse.id_company,
            accounting_account_id: companyAccountingBookingJournalEntriesResponse.filter.accounting_account,
            interval: companyAccountingBookingJournalEntriesResponse.filter.interval,
            start_date: companyAccountingBookingJournalEntriesResponse.filter.start_date,
            end_date: companyAccountingBookingJournalEntriesResponse.filter.end_date
          }))

          const serverPagination = raw?.pagination
          if (serverPagination) {
            const totalPages = serverPagination.pages || 1

            if (page > totalPages && totalPages >= 1 && page !== totalPages) {
              setUrlPage(totalPages)
              loadAccountingBookingJournalEntries(id_company, id_booking_journal, totalPages)
              return
            }

            let pagesOfPagination: number[] = []
            for (let i = 1; i <= totalPages; i++) {
              pagesOfPagination.push(i)
            }
            setPagination(prev => ({...prev, current: serverPagination.page, total: serverPagination.total, pages: pagesOfPagination}))
          }

          setFilter({booking_journal: companyAccountingBookingJournalEntriesResponse.id_booking_journal, options: companyAccountingBookingJournalEntriesResponse.filter})
          setAccountingEntries(companyAccountingBookingJournalEntriesResponse.accounting_entries)
          setStorage((localStorage.getItem('filter_booking_journal_entries')) ? JSON.parse(localStorage.getItem('filter_booking_journal_entries')!) : null)
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      })
    }
  }

  const isEmptyRow = (row: typeof entryRows.value[number]) => !row.debit.id && !row.credit.id && !row.sum

  const submitAccountingEntryCreate = async () => {
    setLoadIndicator('on')

    let entriesIsValid: boolean = entryRows.valid.every((item, idx) => treasuryLockedRows.has(idx) || isEmptyRow(entryRows.value[idx]) || isValidObjectAttributes(item))

    if (companyForUser && settingsForCompany && accounitngEntries && entryDate.valid && entryJournal.valid && entriesIsValid) {
      let responsePromise: Promise<ApiResponse>

      if (type === 'multiple-simplified') {
        const row = entryRows.value[0]
        const concept_account = transaction === 'debit' ? row.debit.id : row.credit.id
        const counterpart_account = transaction === 'debit' ? row.credit.id : row.debit.id
        const amount = parseFloat(row.sum)
        const tax_included_bool = taxIncluded === 'true'
        const company_tax = {
          tax_method: settingsForCompany.tax_method ?? 'effective',
          tax_declaration: settingsForCompany.tax_declaration ?? 'invoicing',
          subject_tax: settingsForCompany.subject_tax ?? false,
        }
        const vit = (transaction === 'debit' && company_tax.tax_method === 'effective') ? vatInputType : undefined

        const rc0 = rowCurrency[0]
        const simpOriginal = rc0?.code && rc0.amount_original && rc0.exchange_rate ? parseFloat(rc0.amount_original) : undefined
        const simpRate = rc0?.code && rc0.amount_original && rc0.exchange_rate ? parseFloat(rc0.exchange_rate) : undefined

        responsePromise = AccountingEntryDA.postSimplifiedEntry(
          companyForUser.id, entryJournal.value, entryDate.value, row.label,
          concept_account, counterpart_account, amount, tax_included_bool, taxCode, transaction,
          company_tax, vit, simpOriginal, simpRate,
        )
      } else {
        const debitList: any[] = []
        const creditList: any[] = []
        entryRows.value.forEach((item, idx) => {
          if (isEmptyRow(item)) return
          const rc = rowCurrency[idx]
          const tc = rowTaxCodes[idx] ?? ''
          if (item.debit.id.length > 0) {
            const line: any = { id_account: item.debit.id, amount: parseFloat(item.sum), description: item.label }
            if (rc?.code && rc.amount_original && rc.exchange_rate) {
              line.amount_original = parseFloat(rc.amount_original)
              line.exchange_rate = parseFloat(rc.exchange_rate)
            }
            if (tc) line.reference = { tax_rates: [tc] }
            debitList.push(line)
          }
          if (item.credit.id.length > 0) {
            const line: any = { id_account: item.credit.id, amount: parseFloat(item.sum), description: item.label }
            if (rc?.code && rc.amount_original && rc.exchange_rate) {
              line.amount_original = parseFloat(rc.amount_original)
              line.exchange_rate = parseFloat(rc.exchange_rate)
            }
            if (tc) line.reference = { tax_rates: [tc] }
            creditList.push(line)
          }
        })

        responsePromise = AccountingEntryDA.postEntry(
          companyForUser.id, entryJournal.value, entryDate.value, type, entryRows.value[0].label, debitList, creditList,
        )
      }

      try {
        const response = await responsePromise

        if (response.status === 201) {
          accountingEntryResponse = normalizeAccountingEntry(response.data, entryJournal.value)

          let tempAccountingEntries: AccountingEntry[] = accounitngEntries
          let index: number = accounitngEntries.findIndex((item) => new Date(item.date) > new Date(accountingEntryResponse.date))

          if (index === -1) {
            tempAccountingEntries.push(accountingEntryResponse);
          } else {
            tempAccountingEntries.splice(index, 0, accountingEntryResponse);
          }

          if (bookingJournal) {
            setBookingJournal({...bookingJournal, total_accounting_entries: bookingJournal.total_accounting_entries + 1})
          }

          // Upload pending vouchers
          let lastVoucherResponse: any = null
          if (vouchers.value.length > 0 && accountingEntryResponse.id) {
            for (const v of vouchers.value) {
              if (v.file) {
                const up = await AccountingEntryDA.uploadVoucher(companyForUser.id, accountingEntryResponse.id, v.file)
                if (up.status === 200 || up.status === 201) {
                  lastVoucherResponse = up.data?.data ?? up.data
                } else {
                  toastError(lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain)
                }
              }
            }
          }

          // Attach pending documents from inbox
          if (pendingAttachments.length > 0 && accountingEntryResponse.id) {
            for (const att of pendingAttachments) {
              const at = await AccountingEntryDA.attachVoucher(companyForUser.id, accountingEntryResponse.id, { id_document_inbox: att.id })
              if (at.status === 200 || at.status === 201) {
                lastVoucherResponse = at.data?.data ?? at.data
              } else {
                toastError(lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain)
              }
            }
            setPendingAttachments([])
          }

          // Update the entry in the list with vouchers from the last upload/attach response
          if (lastVoucherResponse?.vouchers) {
            accountingEntryResponse = { ...accountingEntryResponse, vouchers: lastVoucherResponse.vouchers }
            tempAccountingEntries = tempAccountingEntries.map((item) =>
              item.id === accountingEntryResponse.id ? accountingEntryResponse : item
            )
          }

          setAccountingEntries(tempAccountingEntries)
          setColors({...colors, create: accountingEntryResponse})
          saveLastJournal(entryJournal.value)
          toastSuccess(lang.labels.successfullyCreatedAccountingEntry)
          uploadData()
        } else {
          errorResponse = response.data
          toastError(getError(errorResponse.code, lang.code, errorResponse.message))
        }
      } catch (error) {
        errorWebHandler(lang, error)
      } finally {
        setLoadIndicator('off')
      }
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!entryDate.value) legendInvalid('legend-date-required')
        if (!entryJournal.value) legendInvalid('legend-journal-required')
        if (!entriesIsValid) {
          for (let i = 0; i < entryRows.value.length; i++) {
            if (treasuryLockedRows.has(i)) continue
            if (isEmptyRow(entryRows.value[i])) continue
            const hasDebit = !!entryRows.value[i].debit.id
            const hasCredit = !!entryRows.value[i].credit.id
            if (type === 'multiple') {
              if (!hasDebit && !hasCredit) legendInvalid(`legend-debit-required${suffixLegend(i)}`)
            } else {
              if (!hasDebit) legendInvalid(`legend-debit-required${suffixLegend(i)}`)
              if (!hasCredit) legendInvalid(`legend-credit-required${suffixLegend(i)}`)
            }
            if (!entryRows.value[i].label) legendInvalid(`legend-label-required${suffixLegend(i)}`)
            if (!entryRows.value[i].sum) legendInvalid(`legend-sum-required${suffixLegend(i)}`)
          }
        }
        setLoadIndicator('off')
      })
    }
  }

  const submitAccountingEntryUpdate = async () => {
    setLoadIndicator('on')

    let entriesIsValid: boolean = entryRows.valid.every((item, idx) => treasuryLockedRows.has(idx) || isEmptyRow(entryRows.value[idx]) || isValidObjectAttributes(item))

    // Consistency check: for locked rows with currency, verify amount_original / exchange_rate ≈ CHF
    for (let i = 0; i < entryRows.value.length; i++) {
      if (!treasuryLockedRows.has(i)) continue
      const rc = rowCurrency[i]
      if (!rc?.code || !rc.amount_original || !rc.exchange_rate) continue
      const orig = parseFloat(rc.amount_original)
      const rate = parseFloat(rc.exchange_rate)
      const chf = parseFloat(entryRows.value[i]?.sum)
      if (isNaN(orig) || isNaN(rate) || isNaN(chf) || rate === 0) continue
      const expectedChf = orig / rate
      if (Math.abs(expectedChf - chf) > 0.02) {
        setLoadIndicator('off')
        toastError(`#${i + 1}: ${rc.code} ${orig} / ${rate} = ${expectedChf.toFixed(2)} CHF ≠ ${chf} CHF`)
        return
      }
    }

    if (companyForUser && settingsForCompany && accounitngEntries && entry.valid && entriesIsValid) {
      let responsePromise: Promise<ApiResponse>

      if (type === 'multiple-simplified') {
        const row = entryRows.value[0]
        const concept_account = transaction === 'debit' ? row.debit.id : row.credit.id
        const counterpart_account = transaction === 'debit' ? row.credit.id : row.debit.id
        const amount = parseFloat(row.sum)
        const tax_included_bool = taxIncluded === 'true'
        const company_tax = {
          tax_method: settingsForCompany.tax_method ?? 'effective',
          tax_declaration: settingsForCompany.tax_declaration ?? 'invoicing',
          subject_tax: settingsForCompany.subject_tax ?? false,
        }
        const vit = (transaction === 'debit' && company_tax.tax_method === 'effective') ? vatInputType : undefined

        const rc0u = rowCurrency[0]
        const simpOriginalU = rc0u?.code && rc0u.amount_original && rc0u.exchange_rate ? parseFloat(rc0u.amount_original) : undefined
        const simpRateU = rc0u?.code && rc0u.amount_original && rc0u.exchange_rate ? parseFloat(rc0u.exchange_rate) : undefined

        responsePromise = AccountingEntryDA.putSimplifiedEntry(
          companyForUser.id, entry.value, entryJournal.value, entryDate.value, row.label,
          concept_account, counterpart_account, amount, tax_included_bool, taxCode, transaction,
          company_tax, vit, simpOriginalU, simpRateU,
        )
      } else {
        const debitList: any[] = []
        const creditList: any[] = []
        const loaded = loadedEntryRef.current
        let loadedDebitIdx = 0
        let loadedCreditIdx = 0
        entryRows.value.forEach((item, idx) => {
          if (isEmptyRow(item)) return
          const rc = rowCurrency[idx]
          const tc = rowTaxCodes[idx] ?? ''
          if (item.debit.id.length > 0) {
            const orig = loaded?.debits?.[loadedDebitIdx]
            const line: any = { ...(orig ?? {}), id_account: item.debit.id, amount: parseFloat(item.sum), description: item.label }
            if (rc?.code && rc.amount_original && rc.exchange_rate) {
              line.amount_original = parseFloat(rc.amount_original)
              line.exchange_rate = parseFloat(rc.exchange_rate)
            }
            if (tc) {
              line.reference = { ...(orig?.reference ?? {}), tax_rates: [tc] }
            } else if (orig?.reference) {
              const { tax_rates, ...rest } = orig.reference
              line.reference = Object.keys(rest).length > 0 ? rest : undefined
            }
            loadedDebitIdx++
            debitList.push(line)
          }
          if (item.credit.id.length > 0) {
            const orig = loaded?.credits?.[loadedCreditIdx]
            const line: any = { ...(orig ?? {}), id_account: item.credit.id, amount: parseFloat(item.sum), description: item.label }
            if (rc?.code && rc.amount_original && rc.exchange_rate) {
              line.amount_original = parseFloat(rc.amount_original)
              line.exchange_rate = parseFloat(rc.exchange_rate)
            }
            if (tc) {
              line.reference = { ...(orig?.reference ?? {}), tax_rates: [tc] }
            } else if (orig?.reference) {
              const { tax_rates, ...rest } = orig.reference
              line.reference = Object.keys(rest).length > 0 ? rest : undefined
            }
            loadedCreditIdx++
            creditList.push(line)
          }
        })

        const rootExtra: any = {}
        if (loaded?.id_bank_statement) rootExtra.id_bank_statement = loaded.id_bank_statement
        if (loaded?.id_bank_statement_line) rootExtra.id_bank_statement_line = loaded.id_bank_statement_line

        console.log('[putEntry] payload', { debitList, creditList, ...rootExtra })
        responsePromise = AccountingEntryDA.putEntry(
          companyForUser.id, entry.value, entryRows.value[0].label, entryDate.value, debitList, creditList, rootExtra,
        )
      }

      await responsePromise.then( (response) => {
        if (response.status === 200) {
          accountingEntryResponse = normalizeAccountingEntry(response.data, entryJournal.value)

          let tempAccountingEntries = accounitngEntries.map((temp_item) => {
            if (temp_item.id === entry.value) {
              temp_item = accountingEntryResponse
            }
            return temp_item
          })

          setAccountingEntries(tempAccountingEntries)
          setColors({...colors, update: accountingEntryResponse})
          saveLastJournal(entryJournal.value)
          toastSuccess(lang.labels.successfullyUpdatedAccountingEntry)
          uploadData()
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!entryDate.value) legendInvalid('legend-date-required')
        if (!entryJournal.value) legendInvalid('legend-journal-required')
        if (!entriesIsValid) {
          for (let i = 0; i < entryRows.value.length; i++) {
            if (treasuryLockedRows.has(i)) continue
            if (isEmptyRow(entryRows.value[i])) continue
            const hasDebit = !!entryRows.value[i].debit.id
            const hasCredit = !!entryRows.value[i].credit.id
            if (type === 'multiple') {
              if (!hasDebit && !hasCredit) legendInvalid(`legend-debit-required${suffixLegend(i)}`)
            } else {
              if (!hasDebit) legendInvalid(`legend-debit-required${suffixLegend(i)}`)
              if (!hasCredit) legendInvalid(`legend-credit-required${suffixLegend(i)}`)
            }
            if (!entryRows.value[i].label) legendInvalid(`legend-label-required${suffixLegend(i)}`)
            if (!entryRows.value[i].sum) legendInvalid(`legend-sum-required${suffixLegend(i)}`)
          }
        }
        setLoadIndicator('off')
      })
    }
  }

  const submitAccountingEntryDelete = async () => {
    if (!companyForUser || !accounitngEntries || !entry.valid) return

    const result = await GlobalSwal.fire({
      title: lang.labels.confirmDeleteAccountingEntry,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.delete,
      cancelButtonText: lang.labels.cancel,
      customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary' }
    })

    if (!result.isConfirmed) return

    setLoadIndicator('on')

    await AccountingEntryDA.deleteEntry(companyForUser.id, entry.value).then( (response) => {
      if (response.status === 200 || response.status === 204) {
        let tempAccountingEntries = accounitngEntries.filter((temp_item) => temp_item.id !== entry.value)

        if (bookingJournal) {
          setBookingJournal({...bookingJournal, total_accounting_entries: Math.max(0, bookingJournal.total_accounting_entries - 1)})
        }

        setAccountingEntries(tempAccountingEntries)
        toastSuccess(lang.labels.deleteAccountingEntry)
        uploadData()
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    }).finally(() => {
      setLoadIndicator('off')
    })
  }

  const handleVouchersChanged = (updatedEntity: any) => {
    if (!accounitngEntries || !entry.valid) return
    const updatedVouchers = updatedEntity?.vouchers ?? []
    const tempAccountingEntries = accounitngEntries.map((item) => {
      if (item.id === entry.value) {
        return { ...item, vouchers: updatedVouchers }
      }
      return item
    })
    setAccountingEntries(tempAccountingEntries)
  }

  // Upload pending vouchers when added via modal in edit mode
  useEffect(() => {
    const uploadPendingInEdit = async () => {
      if (entry.valid && entry.value && vouchers.value.length > 0 && companyForUser) {
        for (const v of vouchers.value) {
          if (v.file) {
            const up = await AccountingEntryDA.uploadVoucher(companyForUser.id, entry.value, v.file)
            if (up.status === 200 || up.status === 201) {
              handleVouchersChanged(up.data?.data ?? up.data)
            }
          }
        }
        setVouchers({list: false, value: [], valid: []})
      }
    }
    uploadPendingInEdit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vouchers.value.length])

  const selectedEntry = entry.valid && accounitngEntries
    ? accounitngEntries.find((item) => item.id === entry.value) ?? null
    : null

  const voucherRecord = selectedEntry && companyForUser
    ? { ...selectedEntry, company: { id: companyForUser.id, name: companyForUser.name } }
    : null

  const voucherList: Voucher[] = selectedEntry?.vouchers ?? []

  const executeOpenVoucher = async (item: Voucher) => {
    if (item.id_document_inbox && companyForUser) {
      const signedUrl = await MediaDA.getDocumentSignedUrl(companyForUser.id, item.id_document_inbox)
      if (signedUrl) window.open(signedUrl, '_blank')
    } else if (item.file) {
      window.open(item.file, '_blank')
    }
  }

  const executeDownloadVoucher = async (item: Voucher) => {
    if (item.id_document_inbox && companyForUser) {
      const signedUrl = await MediaDA.getDocumentSignedUrl(companyForUser.id, item.id_document_inbox)
      if (signedUrl) FileSaver.saveAs(signedUrl, attachmentPdfFilename(item.original_name ?? item.name, 'voucher'))
    } else if (item.file) {
      FileSaver.saveAs(item.file, attachmentPdfFilename(item.original_name ?? item.name, 'voucher'))
    }
  }

  const executeDeleteVoucher = async (index: number) => {
    if (!companyForUser || !entry.valid || !entry.value) return
    const result = await GlobalSwal.fire({
      text: lang.labels.confirmRemoveVoucher,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesConfirm,
      cancelButtonText: lang.labels.noCancel
    })
    if (!result.isConfirmed) return
    try {
      const response = await AccountingEntryDA.deleteVoucher(companyForUser.id, entry.value, index)
      if (response?.status === 200) {
        handleVouchersChanged(response.data?.data ?? response.data)
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const handleChangeSearch = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let text: string = event.target.value
    let list: AccountingAccount[] = []

    if (companyForUser && accountingAccountsForCompany) {
      if (text.length > 0) {
        for (let accountingAccount of accountingAccountsForCompany) {
          if (accountingAccount.code.indexOf(text) !== -1 || formatAccountingAccount(accountingAccount, companyForUser.language).toLowerCase().indexOf(text.toLowerCase()) !== -1) {
            list.push(accountingAccount)
          }
        }
      } else {
        list = accountingAccountsForCompany
      }
    }

    setSearch({text: text, list: list})
  }

  const handleChangeType = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let entryValue = {debit: {id: '', text: ''}, credit: {id: '', text: ''}, label: '', sum: ''}
    let entryValid = {debit: false, credit: false, label: false, sum: false}

    setType(event.target.checked ? 'multiple' : 'simple')
    restartForm()

    if (event.target.checked) {
      setEntryRows({value: [entryValue, entryValue], valid: [entryValid, entryValid]})
      setRowTaxCodes(['', ''])
    } else {
      setEntryRows({value: [entryValue], valid: [entryValid]})
      setRowTaxCodes([''])
    }

    if (entry.valid && entry.value.length > 0){
      setEntry({value: '', valid: false})
      setEntryDate({value: accountingEntryResponse ? accountingEntryResponse.date : currentDate(), valid: true})

      if (bookingJournal === null) {
        setEntryJournal(resolveDefaultJournal())
      }
    }

    setTaxCode('')
    setTaxRate(0)
    setTaxIncluded('')
    setTransaction('')
    setVatInputType('goods')
  }

  const handleChangeTaxIncluded = (event: React.MouseEvent <HTMLFormElement | HTMLInputElement>) => {
    const value = (taxIncluded === (event.target as HTMLInputElement).value) ? '' : (event.target as HTMLInputElement).value

    setTaxIncluded(value)
    setType(value.length === 0 && taxCode.length === 0 && transaction.length === 0 ? 'simple' : 'multiple-simplified')
  }

  const handleChangeTransaction = (event: React.MouseEvent <HTMLFormElement | HTMLInputElement>) => {
    const value = (transaction === (event.target as HTMLInputElement).value) ? '' : (event.target as HTMLInputElement).value

    setTransaction(value)
    setType(value.length === 0 && taxCode.length === 0 && taxIncluded.length === 0 ? 'simple' : 'multiple-simplified')
  }

  const handleChangeDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    if (event.target.value.length > 0) {
      setEntryDate({value: event.target.value, valid: true})
      legendValid('legend-date-required')
    } else {
      setEntryDate({value: event.target.value, valid: false})
      legendInvalid('legend-date-required')
    }
  }

  const handleChangeLabel = (index: number, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let tempRowsValue = entryRows.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        return {...temp_item, label: event.target.value}
      }
      return temp_item
    })

    setEntryRows({...entryRows, value: tempRowsValue})
  }

  const handleChangeSum = (index: number, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let tempRowsValue = entryRows.value.map((temp_item, temp_index) => {
      if (index === temp_index) {
        return {...temp_item, sum: event.target.value}
      }
      return temp_item
    })

    setEntryRows({...entryRows, value: tempRowsValue})
  }

  const handleChangeDebit = (index: number, option: {value: string, label: string} | null) => {
    if (companyForUser) {
      if (option) {
        if (type === 'multiple' && option.value === 'none') {
          if (entry.valid && entry.value.length > 0) {
            let tempRowsValue = entryRows.value.map((temp_item, temp_index) => {
              if (temp_index === index) {
                return {...temp_item, debit: {...temp_item.debit, id: '', text: ''}}
              }
              return temp_item
            })
            let tempRowsValid = entryRows.valid.map((temp_item, temp_index) => {
              if (temp_index === index) {
                return {...temp_item, debit: false, credit: false}
              }
              return temp_item
            })
            setEntryRows({value: tempRowsValue, valid: tempRowsValid})
          }
          focusNext(`input-credit${suffixLegend(index)}`, true)
        } else {
          let tempRowsValue = entryRows.value.map((temp_item, temp_index) => {
            if (temp_index === index) {
              return {...temp_item, debit: {...temp_item.debit, id: option.value, text: option.label}}
            }
            return temp_item
          })
          let tempRowsValid = entryRows.valid.map((temp_item, temp_index) => {
            if (temp_index === index) {
              return {...temp_item, debit: true, credit: type === 'multiple' ? true : entryRows.valid[index].credit}
            }
            return temp_item
          })

          setEntryRows({value: tempRowsValue, valid: tempRowsValid})
          legendValid(`legend-debit-required${suffixLegend(index)}`)
          compareDebitCredit(tempRowsValue, tempRowsValid)
          detectRowCurrency(index, option.value, entryRows.value[index].credit.id)

          if (type === 'multiple') {
            focusNext(`input-label${suffixLegend(index)}`, false)
          } else {
            focusNext(`input-credit${suffixLegend(index)}`, true)
          }
        }
      } else {
        let tempRowsValue = entryRows.value.map((temp_item, temp_index) => {
          if (temp_index === index) {
            return {...temp_item, debit: {id: '', text: ''}}
          }
          return temp_item
        })
        let tempRowsValid = entryRows.valid.map((temp_item, temp_index) => {
          if (temp_index === index) {
            return {...temp_item, debit: false, credit: type === 'multiple' ? false : entryRows.valid[index].credit}
          }
          return temp_item
        })

        setEntryRows({value: tempRowsValue, valid: tempRowsValid})
        legendInvalid(`legend-debit-required${suffixLegend(index)}`)
        compareDebitCredit(tempRowsValue, tempRowsValid)
        detectRowCurrency(index, '', entryRows.value[index].credit.id)
      }
    }
  }

  const handleChangeCredit = (index: number, option: {value: string, label: string} | null) => {
    if (companyForUser) {
      if (option) {
        if (type === 'multiple' && option.value === 'none'){
          if (entry.valid && entry.value.length > 0) {
            let tempRowsValue = entryRows.value.map((temp_item, temp_index) => {
              if (temp_index === index) {
                return {...temp_item, credit: {...temp_item.debit, id: '', text: ''}}
              }
              return temp_item
            })
            let tempRowsValid = entryRows.valid.map((temp_item, temp_index) => {
              if (temp_index === index) {
                return {...temp_item, debit: false, credit: false}
              }
              return temp_item
            })
            setEntryRows({value: tempRowsValue, valid: tempRowsValid})
          }

          focusNext(`input-label${suffixLegend(index)}`, false)
        } else {
          let tempRowsValue = entryRows.value.map((temp_item, temp_index) => {
            if (temp_index === index) {
              return {...temp_item, credit: {...temp_item.credit, id: option.value, text: option.label}}
            }
            return temp_item
          })
          let tempRowsValid = entryRows.valid.map((temp_item, temp_index) => {
            if (temp_index === index) {
              return {...temp_item, credit: true, debit: type === 'multiple' ? true : entryRows.valid[index].debit}
            }
            return temp_item
          })

          setEntryRows({value: tempRowsValue, valid: tempRowsValid})
          legendValid(`legend-credit-required${suffixLegend(index)}`)
          compareDebitCredit(tempRowsValue, tempRowsValid)
          detectRowCurrency(index, entryRows.value[index].debit.id, option.value)

          focusNext(`input-label${suffixLegend(index)}`, false)
        }
      } else {
        let tempRowsValue = entryRows.value.map((temp_item, temp_index) => {
          if (temp_index === index) {
            return {...temp_item, credit: {id: '', text: ''}}
          }
          return temp_item
        })
        let tempRowsValid = entryRows.valid.map((temp_item, temp_index) => {
          if (temp_index === index) {
            return {...temp_item, credit: false, debit: type === 'multiple' ? false : entryRows.valid[index].debit}
          }
          return temp_item
        })

        setEntryRows({value: tempRowsValue, valid: tempRowsValid})
        legendInvalid(`legend-credit-required${suffixLegend(index)}`)
        compareDebitCredit(tempRowsValue, tempRowsValid)
        detectRowCurrency(index, entryRows.value[index].debit.id, '')
      }
    }
  }

  const handleChangeJournal = (option: {value: string, label: string} | null) => {
    userTouchedJournalRef.current = true
    if (option) {
      setEntryJournal({value: option.value, valid: true})
      legendValid('legend-journal-required')
      focusNext('input-label-0', false)
    } else {
      setEntryJournal({value: '', valid: false})
      legendInvalid('legend-journal-required')
    }
  }

  const handleChangeTaxFee = (item: string, value: number) => {
    setTaxCode(item)
    setTaxRate(value)
    setType(item.length === 0 && taxIncluded.length === 0 && transaction.length === 0 ? 'simple' : 'multiple-simplified')
  }

  const handleChangeRowTaxCode = (index: number, code: string) => {
    setRowTaxCodes(prev => {
      const next = [...prev]
      next[index] = code
      return next
    })
  }

  const detectRowCurrency = (index: number, debitId: string, creditId: string) => {
    if (!accountingAccountsForCompany) return
    const debitAcc = debitId ? accountingAccountsForCompany.find(a => a.id === debitId) : null
    const creditAcc = creditId ? accountingAccountsForCompany.find(a => a.id === creditId) : null
    const foreignAcc = debitAcc?.currency?.code && debitAcc.currency.code !== 'CHF' ? debitAcc
      : creditAcc?.currency?.code && creditAcc.currency.code !== 'CHF' ? creditAcc
      : null
    const code = foreignAcc?.currency?.code ?? ''

    setRowCurrency(prev => {
      const next = [...prev]
      while (next.length <= index) next.push({code: '', amount_original: '', exchange_rate: ''})
      if (next[index].code !== code) {
        next[index] = {code, amount_original: '', exchange_rate: ''}
        if (code && companyForUser) {
          ExchangeRateDA.getRate(companyForUser.id, code, 'CHF').then(res => {
            if (res.status === 200 && res.data?.rate) {
              setRowCurrency(prev2 => {
                const n = [...prev2]
                if (n[index]?.code === code) {
                  n[index] = {...n[index], exchange_rate: String(res.data.rate)}
                }
                return n
              })
            }
          })
        }
      }
      return next
    })
  }

  const handleChangeAmountOriginal = (index: number, value: string) => {
    const original = parseFloat(value)
    if (treasuryLockedRows.has(index)) {
      const chf = parseFloat(entryRows.value[index]?.sum)
      setRowCurrency(prev => {
        const next = [...prev]
        const derived = (!isNaN(original) && original > 0 && !isNaN(chf) && chf > 0)
          ? (original / chf).toFixed(6) : prev[index]?.exchange_rate ?? ''
        next[index] = {...next[index], amount_original: value, exchange_rate: derived}
        return next
      })
    } else {
      setRowCurrency(prev => {
        const next = [...prev]
        next[index] = {...next[index], amount_original: value}
        return next
      })
      const rate = parseFloat(rowCurrency[index]?.exchange_rate)
      if (!isNaN(rate) && rate > 0 && !isNaN(original)) {
        const chf = (original / rate).toFixed(2)
        const tempRowsValue = entryRows.value.map((item, i) => i === index ? {...item, sum: chf} : item)
        setEntryRows({...entryRows, value: tempRowsValue})
      }
    }
  }

  const handleChangeExchangeRate = (index: number, value: string) => {
    const rate = parseFloat(value)
    if (treasuryLockedRows.has(index)) {
      const chf = parseFloat(entryRows.value[index]?.sum)
      setRowCurrency(prev => {
        const next = [...prev]
        const derived = (!isNaN(rate) && rate > 0 && !isNaN(chf) && chf > 0)
          ? (chf * rate).toFixed(2) : prev[index]?.amount_original ?? ''
        next[index] = {...next[index], exchange_rate: value, amount_original: derived}
        return next
      })
    } else {
      setRowCurrency(prev => {
        const next = [...prev]
        next[index] = {...next[index], exchange_rate: value}
        return next
      })
      const original = parseFloat(rowCurrency[index]?.amount_original)
      if (!isNaN(rate) && rate > 0 && !isNaN(original)) {
        const chf = (original / rate).toFixed(2)
        const tempRowsValue = entryRows.value.map((item, i) => i === index ? {...item, sum: chf} : item)
        setEntryRows({...entryRows, value: tempRowsValue})
      }
    }
  }

  const validateLabel = (index: number) => {
    if (treasuryLockedRows.has(index)) return
    evaluateLegendListObjectRequired(expressions.text, index, 'label', entryRows, setEntryRows, `legend-label-valid${suffixLegend(index)}`, `legend-label-required${suffixLegend(index)}`)
  }

  const validateSum = (index: number) => {
    if (treasuryLockedRows.has(index)) return
    evaluateLegendListObjectRequired(expressions.currency, index, 'sum', entryRows, setEntryRows, `legend-sum-valid${suffixLegend(index)}`, `legend-sum-required${suffixLegend(index)}`)
    compareDebitCredit(entryRows.value, entryRows.valid)
  }

  const executeEnterSum = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      if (entryRows.value.length - 1 > index) {
        focusEnter(`input-debit${suffixLegend(index + 1)}`, true, event)
      } else {
        let element = document.getElementById(`input-sum${suffixLegend(index)}`) as HTMLInputElement
        element.blur()

        if (entry.valid && entry.value.length > 0) {
          submitAccountingEntryUpdate()
        } else {
          submitAccountingEntryCreate()
        }
      }
    }
  }

  const executeSelectAccountingEntry = (item: AccountingEntry) => {
    if (companyForUser && accountingAccountsForCompany && !item.automated) {
      const isTreasuryLine = (l: any) => l.type === 'treasury-account' || l.reference?.id_treasury_account
      const isReconciliationEntry = (item as any).id_bank_statement_line
        || item.debits.some((l: any) => isTreasuryLine(l))
        || item.credits.some((l: any) => isTreasuryLine(l))

      const effectiveType = isReconciliationEntry ? 'multiple' : item.type

      loadedEntryRef.current = item
      setType(effectiveType)
      setEntry({value: item.id, valid: true})
      setEntryDate({value: item.date, valid: true})
      setEntryJournal({value: item.id_booking_journal, valid: true})

      // Helper: resolve currency data from a line's account
      const lineCurrency = (line: any): {code: string, amount_original: string, exchange_rate: string} => {
        const acc = accountingAccountsForCompany.find(a => a.id === line.id_account)
        const code = acc?.currency?.code && acc.currency.code !== 'CHF' ? acc.currency.code : ''
        return {
          code,
          amount_original: line.amount_original != null ? String(line.amount_original) : '',
          exchange_rate: line.exchange_rate != null ? String(line.exchange_rate) : '',
        }
      }

      switch (effectiveType) {
        case 'simple': {
          setTaxCode('')
          setTaxRate(0)
          setTaxIncluded('')
          setTransaction('')
          setTreasuryLockedRows(new Set())
          setEntryRows({value: [{debit: {id: item.debits[0].id_account, text: accountingAccountSelected(accountingAccountsForCompany, item.debits[0].id_account, companyForUser.language)}, credit: {id: item.credits[0].id_account, text: accountingAccountSelected(accountingAccountsForCompany, item.credits[0].id_account, companyForUser.language)}, label: item.debits[0].description, sum: item.debits[0].amount.toString()}], valid: [{debit: true, credit: true, label: true, sum: true}]})
          const dCur = lineCurrency(item.debits[0])
          const cCur = lineCurrency(item.credits[0])
          setRowCurrency([dCur.code ? dCur : cCur.code ? cCur : {code: '', amount_original: '', exchange_rate: ''}])
          setRowTaxCodes([''])
          break
        }
        case 'multiple': {
          let tempDebitsValue = item.debits.map((temp_item) => ({debit: {id: temp_item.id_account, text: accountingAccountSelected(accountingAccountsForCompany, temp_item.id_account, companyForUser.language)}, credit: {id: '', text: ''}, label: temp_item.description, sum: temp_item.amount.toString()}))
          let tempCreditsValue = item.credits.map((temp_item) => ({debit: {id: '', text: ''}, credit: {id: temp_item.id_account, text: accountingAccountSelected(accountingAccountsForCompany, temp_item.id_account, companyForUser.language)}, label: temp_item.description, sum: temp_item.amount.toString()}))
          let tempDebitsValid = item.debits.map(() => ({debit: true, credit: true, label: true, sum: true}))
          let tempCreditsValid = item.credits.map(() => ({debit: true, credit: true, label: true, sum: true}))
          setTaxCode('')
          setTaxRate(0)
          setTaxIncluded('')
          setTransaction('')
          setEntryRows({value: [...tempDebitsValue, ...tempCreditsValue], valid: [...tempDebitsValid, ...tempCreditsValid]})
          setRowCurrency([
            ...item.debits.map((d: any) => lineCurrency(d)),
            ...item.credits.map((c: any) => lineCurrency(c)),
          ])
          setRowTaxCodes([
            ...item.debits.map((d: any) => d.reference?.tax_rates?.[0] ?? ''),
            ...item.credits.map((c: any) => c.reference?.tax_rates?.[0] ?? ''),
          ])
          {
            const locked = new Set<number>()
            item.debits.forEach((l: any, i: number) => { if (isTreasuryLine(l)) locked.add(i) })
            item.credits.forEach((l: any, i: number) => { if (isTreasuryLine(l)) locked.add(item.debits.length + i) })
            setTreasuryLockedRows(locked)
          }
          break
        }
        case 'multiple-simplified': {
          const VAT_ACCOUNT_CODES = ['2200', '1170', '1171']
          const allLines = [
            ...item.debits.map(l => ({ ...l, side: 'debit' as const })),
            ...item.credits.map(l => ({ ...l, side: 'credit' as const })),
          ]

          const taxLine = allLines.find(l => VAT_ACCOUNT_CODES.includes(accountingAccountCode(accountingAccountsForCompany, l.id_account)))

          const conceptLine = allLines.find(l =>
            l !== taxLine &&
            Array.isArray(l.reference?.tax_rates) && l.reference!.tax_rates!.length > 0
          )

          const counterpartLine = allLines.find(l =>
            l !== taxLine &&
            l !== conceptLine &&
            (!Array.isArray(l.reference?.tax_rates) || l.reference!.tax_rates!.length === 0)
          )

          const inferredTransaction: 'debit' | 'credit' = conceptLine?.side === 'credit' ? 'credit' : 'debit'

          const inferredTaxCode = (taxLine?.reference?.tax_rates?.[0]) ?? (conceptLine?.reference?.tax_rates?.[0]) ?? ''
          const inferredTaxRate = (settingsForCompany?.tax_fees ?? []).find((t: any) => t.code === inferredTaxCode)?.rate ?? 0

          const taxCodeOfAccount = taxLine ? accountingAccountCode(accountingAccountsForCompany, taxLine.id_account) : ''
          const inferredVatInputType: 'goods' | 'investment' =
            (inferredTransaction === 'debit' && taxCodeOfAccount === '1171') ? 'investment' : 'goods'

          setTaxCode(inferredTaxCode)
          setTaxRate(inferredTaxRate)
          setTaxIncluded('false')
          setTransaction(inferredTransaction)
          setVatInputType(inferredVatInputType)

          const conceptForRow = conceptLine ?? allLines.find(l => l !== taxLine) ?? allLines[0]
          const counterpartForRow = counterpartLine ?? allLines.find(l => l !== taxLine && l !== conceptForRow) ?? null

          const debitAccountId = inferredTransaction === 'credit'
            ? (counterpartForRow?.id_account ?? '')
            : (conceptForRow?.id_account ?? '')
          const creditAccountId = inferredTransaction === 'credit'
            ? (conceptForRow?.id_account ?? '')
            : (counterpartForRow?.id_account ?? '')

          const conceptAmount = conceptForRow?.amount ?? 0

          setTreasuryLockedRows(new Set())
          setEntryRows({
            value: [{
              debit:  { id: debitAccountId,  text: debitAccountId  ? accountingAccountSelected(accountingAccountsForCompany, debitAccountId,  companyForUser.language) : '' },
              credit: { id: creditAccountId, text: creditAccountId ? accountingAccountSelected(accountingAccountsForCompany, creditAccountId, companyForUser.language) : '' },
              label:  conceptForRow?.description ?? '',
              sum:    conceptAmount.toString(),
            }],
            valid: [{ debit: true, credit: true, label: true, sum: true }],
          })
          {
            const foreignLine = [conceptForRow, counterpartForRow].find((l: any) => {
              if (!l) return false
              const acc = accountingAccountsForCompany.find(a => a.id === l.id_account)
              return acc?.currency?.code && acc.currency.code !== 'CHF'
            })
            setRowCurrency([foreignLine ? lineCurrency(foreignLine) : {code: '', amount_original: '', exchange_rate: ''}])
          }
          setRowTaxCodes([''])
          break
        }
      }

      let element = document.getElementById('input-date') as HTMLInputElement
      element.focus()
    }
  }

  const executeAccountingBookingJournalEntries = (item: string) => {
    if (companyForUser) {
      setUrlPage(1)
      loadAccountingBookingJournalEntries(companyForUser.id, item, 1)
    }
  }

  const executeAccountingAccountEntries = (item: string) => {
    if (companyForUser) {
      filter.options.accounting_account = item
      setUrlPage(1)
      loadAccountingBookingJournalEntries(companyForUser.id, filter.booking_journal, 1)
    }
  }

  const syncFilterToUrl = () => {
    setSp(prev => {
      const next = new URLSearchParams(prev)
      const from = filter.options.start_date || ''
      const to = filter.options.end_date || ''
      if (from) { next.set('from', from) } else { next.delete('from') }
      if (to) { next.set('to', to) } else { next.delete('to') }
      return next
    }, { replace: true })
  }

  const loadPagination = (current: number) => {
    if (!companyForUser || !param.id) return
    setUrlPage(current)
    loadAccountingBookingJournalEntries(companyForUser.id, param.id, current)
  }

  const handleExport = async (format: 'xlsx' | 'pdf') => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    if (!id || exporting) return
    setExporting(true)
    try {
      const journalId = param.id === 'all' ? '' : (param.id ?? '')
      const period = [filter.options.start_date?.substring(0, 10), filter.options.end_date?.substring(0, 10)].filter(Boolean).join('_')
      const fallback = exportFallbackFilename(lang.labels.bookingJournal, companyForUser?.name ?? '', period, format)
      const result = await ReportDA.exportJournal(id, format, {
        id_journal: journalId || undefined,
        id_account: filter.options.accounting_account || undefined,
        date_from: filter.options.start_date || undefined,
        date_to: filter.options.end_date || undefined,
        q: urlQ || undefined,
        lang: lang.code.toLowerCase(),
      }, fallback)
      if (result.blob && result.filename) {
        triggerBlobDownload(result.blob, result.filename)
      } else {
        GlobalSwal.fire({ title: result.error || lang.labels.exportError, icon: 'error', confirmButtonText: lang.labels.okGotIt })
      }
    } catch (error) {
      console.error('[JournalView] export failed', error)
      GlobalSwal.fire({ title: lang.labels.exportError, icon: 'error', confirmButtonText: lang.labels.okGotIt })
    } finally {
      setExporting(false)
    }
  }

  function compareDebitCredit(list_value: typeof entryRows.value, list_valid: typeof entryRows.valid) {
    if (type === 'multiple') {
      let totalDebits = list_value.filter((item) => item.debit.id.length > 0).reduce((sum, item) => sum + Number(item.sum), 0)
      let totalCredits = list_value.filter((item) => item.credit.id.length > 0).reduce((sum, item) => sum + Number(item.sum), 0)

      if (totalDebits !== totalCredits) {
        if (!list_value.some(isEmptyRow)) {
          const prefillLabel = list_value[0]?.label ?? ''
          let entryValue = {debit: {id: '', text: ''}, credit: {id: '', text: ''}, label: prefillLabel, sum: ''}
          let entryValid = {debit: false, credit: false, label: !!prefillLabel, sum: false}
          setEntryRows({...entryRows, value: [...list_value, entryValue], valid: [...list_valid, entryValid]})
          setRowCurrency(prev => [...prev, {code: '', amount_original: '', exchange_rate: ''}])
          setRowTaxCodes(prev => [...prev, ''])
        }
      } else {
        if (list_value.length > 2 && list_valid.length > 2) {
          let filteredValues = list_value.filter((item) => !isEmptyRow(item))
          let filteredValids = list_valid.slice(0, filteredValues.length)
          setEntryRows({...entryRows, value: filteredValues, valid: filteredValids})
          setRowCurrency(prev => prev.slice(0, filteredValues.length))
          setRowTaxCodes(prev => prev.slice(0, filteredValues.length))
        }
      }
    }
  }

  function focusNext(selector: string, is_select: boolean) {
    setTimeout(() => {
      let element = document.getElementById(selector) as HTMLInputElement
      if (!element) return
      element.focus()

      if (is_select) {
        let event = new KeyboardEvent("keydown", {key: "ArrowDown", bubbles: true})
        element.dispatchEvent(event)
      }
    }, 0)
  }

  function focusEnter(selector: string, is_select: boolean, event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault()
      focusNext(selector, is_select)
    }
  }

  function colorAccountingEntry(accounting_entry: AccountingEntry): string {
    if (accounting_entry.automated === false) {
      switch (accounting_entry.id) {
        case entry.value:
          return 'light-warning'
        case colors.create?.id:
          return 'light-success'
        case colors.update?.id:
          return 'light-primary'
        default:
          return 'lighten'
      }
    } else {
      return 'light-danger'
    }
  }

  function currentDate(): string {
    return format((new Date()), 'yyyy-MM-dd')
  }

  function uploadDropdown() {
    if (accountingAccountsForCompany) {
      setSearch({text: '', list: accountingAccountsForCompany})
    }
  }

  function restartForm() {
    legendValidInvalidRestart('legend-date-required')
    legendValidInvalidRestart('legend-journal-required')

    for (let index in entryRows.value) {
      legendValidInvalidRestart(`legend-debit-required${suffixLegend(index)}`)
      legendValidInvalidRestart(`legend-credit-required${suffixLegend(index)}`)
      legendValidRequiredRestart(`legend-label-valid${suffixLegend(index)}`, `legend-label-required${suffixLegend(index)}`)
      legendValidRequiredRestart(`legend-sum-valid${suffixLegend(index)}`, `legend-sum-required${suffixLegend(index)}`)
    }
  }

  function uploadData() {
    setType('simple')
    setTransaction('')
    setTaxIncluded('')
    setTaxCode('')
    setTaxRate(0)
    setVatInputType('goods')
    setVouchers({list: false, value: [], valid: []})
    setPendingAttachments([])
    setEntry({value: '', valid:false})
    setEntryDate({value: accountingEntryResponse ? accountingEntryResponse.date : currentDate(), valid: true})
    setEntryJournal(bookingJournal ? {value: bookingJournal.id, valid: true} : resolveDefaultJournal())
    userTouchedJournalRef.current = false
    setEntryRows({value: [{debit: {id: '', text: ''}, credit: {id: '', text: ''}, label: '', sum: ''}], valid: [{debit: false, credit: false, label: false, sum: false}]})
    setRowCurrency([{code: '', amount_original: '', exchange_rate: ''}])
    setRowTaxCodes([''])
    loadedEntryRef.current = null
    setTreasuryLockedRows(new Set())
    restartForm()
  }

  useEffect( () => {
    if (companyForUser && accountingAccountsForCompany && bookingJournalsForCompany) {
      let defautlDebitsCredits: {value: string, label: string}[] = (type === 'multiple') ? [{value: 'none', label: lang.labels.none}] : []
      let tempJournals: {value: string, label: string}[] = bookingJournalsForCompany.map((item) => ({value: item.id, label: item.name}))
      let tempDebits: {value: string, label: string}[] = [...defautlDebitsCredits, ...accountingAccountsForCompany.map((item) => ({value: item.id, label: formatAccountingAccount(item, companyForUser.language)}))]
      let tempCredits: {value: string, label: string}[] = [...defautlDebitsCredits, ...accountingAccountsForCompany.map((item) => ({value: item.id, label: formatAccountingAccount(item, companyForUser.language)}))]
      setOptions({journal: tempJournals, debit: tempDebits, credit: tempCredits})
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, accountingAccountsForCompany, bookingJournalsForCompany, type])

  // Pre-select default journal when journals arrive and the field is still empty (all view, initial mount)
  useEffect(() => {
    if (bookingJournal === null && !userTouchedJournalRef.current && !entryJournal.value && bookingJournalsForCompany?.length) {
      const resolved = resolveDefaultJournal()
      if (resolved.valid) {
        setEntryJournal(resolved)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingJournalsForCompany])

  useEffect( () => {
    if (!companyForUser || !param.id) return

    const urlFrom = sp.get('from') ?? ''
    const urlTo = sp.get('to') ?? ''

    // Shared continuation: apply resolved filter, sync URL, load entries
    const applyFilter = (start_date: string, end_date: string, interval: string) => {
      filter.options.interval = interval
      filter.options.start_date = start_date
      filter.options.end_date = end_date

      // Sync restored dates to URL once (only if values differ from what's already there)
      if (start_date !== urlFrom || end_date !== urlTo) {
        setSp(prev => {
          const next = new URLSearchParams(prev)
          if (start_date) { next.set('from', start_date) } else { next.delete('from') }
          if (end_date) { next.set('to', end_date) } else { next.delete('to') }
          return next
        }, { replace: true })
      }

      setEntryDate({value: currentDate(), valid: true})
      setAccountingEntries(null)

      loadAccountingBookingJournalEntries(companyForUser.id, param.id)

      if (param.id !== 'all') {
        setBookingJournal(null)
        loadBookingJournal(companyForUser.id, param.id)
      }
    }

    // --- Resolution chain: URL > localStorage > fiscal-year default ---

    // 1. URL search params take priority (survives navigation & reload)
    if (urlFrom || urlTo) {
      applyFilter(urlFrom, urlTo, '')
      return
    }

    // 2. localStorage (cross-session persistence, per company)
    let saved: any = null
    try { saved = JSON.parse(localStorage.getItem('filter_booking_journal_entries') ?? 'null') } catch { /* ignore */ }
    const sameCompany = saved?.company_id === companyForUser.id

    if (sameCompany && saved) {
      const dateCompany: Date = new Date(companyForUser.created_at)
      const yearCurrent: Date = startOfYear(new Date())
      let interval = saved.interval?.length > 0 ? saved.interval : ''
      let start_date = ''
      let end_date = ''
      if (saved.interval === 'all') {
        start_date = format(dateCompany, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
        end_date = format(yearCurrent, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)
      } else {
        start_date = saved.start_date?.length > 0 ? saved.start_date : ''
        end_date = saved.end_date?.length > 0 ? saved.end_date : ''
      }
      applyFilter(start_date, end_date, interval)
      return
    }

    // 3. Default: current fiscal year, then latest fiscal year, then calendar year
    FiscalYearDA.getFiscalYears(companyForUser.id).then(response => {
      if (response.status === 200) {
        const data = response.data
        const fys: any[] = Array.isArray(data) ? data : data?.items ?? data?.fiscal_years ?? []
        const todayStr = format(new Date(), 'yyyy-MM-dd')

        // a. Fiscal year covering today
        let fy = fys.find((f: any) => f.start_date.substring(0, 10) <= todayStr && f.end_date.substring(0, 10) >= todayStr)

        // b. Latest defined fiscal year
        if (!fy && fys.length > 0) {
          fy = [...fys].sort((a: any, b: any) => b.end_date.localeCompare(a.end_date))[0]
        }

        if (fy) {
          applyFilter(fy.start_date.substring(0, 10), fy.end_date.substring(0, 10), '')
          return
        }
      }
      // c. Calendar year fallback
      const year = new Date().getFullYear()
      applyFilter(`${year}-01-01`, `${year}-12-31`, '')
    }).catch(() => {
      const year = new Date().getFullYear()
      applyFilter(`${year}-01-01`, `${year}-12-31`, '')
    })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect(() => {
    if (companyForUser && param.id) {
      loadAccountingBookingJournalEntries(companyForUser.id, param.id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQ])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.accounting, branch: lang.labels.viewBookingJournal}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="d-flex flex-column bd-highlight h-lg-100" style={{height: "calc(100% - 62px)"}}>
      { companyForUser && settingsForCompany && accountingAccountsForCompany && bookingJournalsForCompany && param.id
        ?
        <>
          <div className="card card-flush bd-highlight mb-5">
            <div className="card-header">
              <div className="card-title">
                <div className="d-flex align-items-center">
                  <div className="symbol symbol-circle me-3">
                    <div className="symbol-label bg-transparent border border-secondary border-dashed">
                      <span className="svg-icon svg-icon-3x svg-icon-primary">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M22 8H8L12 4H19C19.6 4 20.2 4.39999 20.5 4.89999L22 8ZM3.5 19.1C3.8 19.7 4.4 20 5 20H12L16 16H2L3.5 19.1ZM19.1 20.5C19.7 20.2 20 19.6 20 19V12L16 8V22L19.1 20.5ZM4.9 3.5C4.3 3.8 4 4.4 4 5V12L8 16V2L4.9 3.5Z" fill="black"/>
                          <path d="M22 8L20 12L16 8H22ZM8 16L4 12L2 16H8ZM16 16L12 20L16 22V16ZM8 8L12 4L8 2V8Z" fill="black"/>
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div className="d-flex flex-column">
                    <h4 className="mb-0">{bookingJournal ? bookingJournal.name : lang.labels.generalLedger}</h4>
                    <div className="text-muted fw-bold">
                      <small className="text-primary">{lang.labels.bookingJournal}</small>
                      <span className="mx-2">|</span>
                      <small className="text-primary">{lang.labels.accountingEntries}</small>
                      <span className="mx-2">|</span>
                      <small className="fw-bolder">{bookingJournal ? bookingJournal.total_accounting_entries : pagination.total > 0 ? pagination.total : accounitngEntries ? accounitngEntries.length : 0} {lang.labels.items}</small>
                    </div> 
                  </div>
                </div>
              </div>
              <div className="card-toolbar">
                <div className="d-flex justify-content-end align-items-center">
                  { param.id === "all" &&
                    <Dropdown>
                      <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-175px p-0 me-2">
                        <span className="selection">
                          <span className="select2-selection select2-selection--single form-select form-select-sm">
                            <span className="select2-selection__rendered" role="textbox">
                              {resolveJournalName(filter.booking_journal)}
                            </span>
                          </span>
                        </span>
                      </Dropdown.Toggle>
                      <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100" align="end">
                        <span className="select2-dropdown select2-dropdown--below">
                          <span className="select2-results">
                            <ul className="select2-results__options" role="listbox">
                              <li className={`select2-results__option select2-results__option--selectable ${filter.booking_journal === "all" && "select2-results__option--selected"}`} role="option" aria-selected={filter.booking_journal === "all"}>
                                <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => executeAccountingBookingJournalEntries("all")}>{lang.labels.all}</Dropdown.Item>
                              </li>
                              { bookingJournalsForCompany.map (( (item, index) => { return (
                                <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === filter.booking_journal && "select2-results__option--selected"}`} role="option" aria-selected={item.id === filter.booking_journal}>
                                  <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => executeAccountingBookingJournalEntries(item.id)}>{item.name}</Dropdown.Item>
                                </li>
                              )}))}
                            </ul>
                          </span>
                        </span>
                      </Dropdown.Menu>
                    </Dropdown>
                  }
                  <Dropdown onToggle={uploadDropdown}>
                    <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-175px p-0 me-2">
                      <span className="selection">
                        <span className="select2-selection select2-selection--single form-select form-select-sm">
                          <span className="select2-selection__rendered" role="textbox">
                            {filter.options.accounting_account.length > 0 ? accountingAccountSelected(accountingAccountsForCompany, filter.options.accounting_account, companyForUser.language) : lang.labels.accountingAccount}
                          </span>
                        </span>
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                      <span className="select2-dropdown select2-dropdown--below">
                        <span className="select2-search select2-search--dropdown">
                          <input className="select2-search__field" type="text" autoComplete="off" name="account" value={search.text} onChange={handleChangeSearch} />
                        </span>
                        <span className="select2-results">
                          <ul className="select2-results__options" role="listbox">
                            { search.text.length === 0 &&
                              <li className={`select2-results__option select2-results__option--selectable ${filter.options.accounting_account === "" && "select2-results__option--selected"}`} role="option" aria-selected={filter.options.accounting_account === ""}>
                              <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => executeAccountingAccountEntries("")}>{lang.labels.all}</Dropdown.Item>
                            </li>
                            }
                            { search.list.length > 0
                              ?
                              <>
                                { search.list.map (( (item, index) => { return (
                                  <li key={index} className={`select2-results__option select2-results__option--selectable ${item.id === filter.options.accounting_account && "select2-results__option--selected"}`} role="option" aria-selected={item.id === filter.options.accounting_account}>
                                    <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => executeAccountingAccountEntries(item.id)}>{formatAccountingAccount(item, companyForUser.language)}</Dropdown.Item>
                                  </li>
                                )}))}
                              </>
                              :
                              <li className="select2-results__option select2-results__message" role="alert">{lang.labels.noResultsFound}</li>
                            }
                          </ul>
                        </span>
                      </span>
                    </Dropdown.Menu>
                  </Dropdown>
                  <AppAccountingFilterInterval storage={storage} filter={filter} setFilter={setFilter} loadAccountingEntries={() => { setUrlPage(1); syncFilterToUrl(); loadAccountingBookingJournalEntries(companyForUser.id, param.id!, 1) }}></AppAccountingFilterInterval>
                  <DropdownActions
                    label={exporting ? `${lang.labels.export}...` : lang.labels.export}
                    buttonClassName={`btn btn-sm ms-2 ${exporting || !accounitngEntries ? 'btn-light disabled' : 'btn-light btn-active-light-primary'}`}
                    items={[
                      { key: 'xlsx', label: `${lang.labels.excel} (.xlsx)`, onClick: () => handleExport('xlsx') },
                      { key: 'pdf', label: `${lang.labels.pdf} (.pdf)`, onClick: () => handleExport('pdf') },
                    ]}
                  />
                </div>
              </div>
            </div>
            <div className="card-body pt-0 pb-4">
              <div className="position-relative" style={{maxWidth: '400px'}}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder={`${lang.labels.search} (${lang.labels.entryNumber ?? 'n°'}, ${lang.labels.description}, ${lang.labels.voucherNumber ?? 'pièce'}, ${lang.labels.amount})`}
                  value={searchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                {searchText && (
                  <button
                    type="button"
                    className="btn btn-sm btn-icon position-absolute top-50 end-0 translate-middle-y me-1"
                    onClick={handleSearchClear}
                  >
                    <i className="bi bi-x-lg fs-7 text-muted"></i>
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="card bd-highlight h-100 mb-5">
            <div className="card-body h-100 p-0">
              <div className="dataTables_wrapper border border-2 rounded h-100">
                <div id="container-accounting-entry-listing" className="table-responsive rounded">
                  <table className="table align-middle table-row-dashed gx-10 gy-2 mb-2">
                    <thead className="bg-light border-bottom border-bottom-2">
                      <tr className="text-dark text-uppercase fw-bolder fs-7">
                        <th className="w-100px text-start text-nowrap py-3">{lang.labels.date}</th>
                        <th className="w-75px text-start text-nowrap py-3">{lang.labels.entryNumberShort}</th>
                        <th className="min-w-100px text-start text-nowrap py-3">{lang.labels.debit}</th>
                        <th className="min-w-100px text-start text-nowrap py-3">{lang.labels.credit}</th>
                        <th className="min-w-100px text-start text-nowrap py-3">{lang.labels.bookingJournal}</th>
                        <th className="min-w-100px text-start text-nowrap py-3">{lang.labels.label}</th>
                        <th className="min-w-100px text-end text-nowrap py-3">{lang.labels.sum} ({companyForUser.currency?.code})</th>
                        <th className="w-50px text-center py-3"><i className="bi bi-paperclip fs-4"></i></th>
                      </tr>
                    </thead>
                    { accounitngEntries
                      ?
                      <>
                        { accounitngEntries.length > 0
                          ?
                          <>
                            { accounitngEntries.map (( (item, index) => { return (
                              <tbody key={index} className={`text-secondary fw-bold border-bottom bg-${colorAccountingEntry(item)} ${entry.value !== item.id && !item.automated && "bg-hover-light-info"}`} onClick={() => executeSelectAccountingEntry(item)}>
                                { item.debits.length === 1 && item.credits.length === 1
                                  ?
                                  <tr key={index} className="row-dashed">
                                    <td className="text-nowrap">{dateFormat(lang.code, item.date)}</td>
                                    <td className="text-nowrap fs-8">{item.entry_number ?? '—'}</td>
                                    <td><div>{accountingAccountCode(accountingAccountsForCompany, item.debits[0].id_account)}</div><div className="text-muted fs-8">{accountingAccountName(accountingAccountsForCompany, companyForUser.language, item.debits[0].id_account)}</div></td>
                                    <td><div>{accountingAccountCode(accountingAccountsForCompany, item.credits[0].id_account)}</div><div className="text-muted fs-8">{accountingAccountName(accountingAccountsForCompany, companyForUser.language, item.credits[0].id_account)}</div></td>
                                    <td>{resolveJournalName(item.id_booking_journal)}</td>
                                    <td>{(() => { const d = entryDocument(item, lang); if (d.text) { return d.to ? <Link to={d.to} className="text-primary fw-bold" onClick={(e) => e.stopPropagation()}>{d.text}</Link> : <span>{d.text}</span> } return <span>{item.debits[0].description === item.credits[0].description ? item.debits[0].description : `${item.debits[0].description}, ${item.credits[0].description}`}</span> })()}</td>
                                    <td className="text-end text-nowrap">{formatMoney(item.debits[0].amount)}</td>
                                    <td className="text-center"><ButtonEntryDocuments vouchers={item.vouchers} /></td>
                                  </tr>
                                  :
                                  <>
                                    { item.debits.map (( (sub_item, sub_index) => { return (
                                      <tr key={sub_index} className="row-dashed">
                                        <td className="text-nowrap">{sub_index === 0 ? dateFormat(lang.code, item.date) : ""}</td>
                                        <td className="text-nowrap fs-8">{sub_index === 0 ? (item.entry_number ?? '—') : ""}</td>
                                        <td><div>{accountingAccountCode(accountingAccountsForCompany, sub_item.id_account)}</div><div className="text-muted fs-8">{accountingAccountName(accountingAccountsForCompany, companyForUser.language, sub_item.id_account)}</div></td>
                                        <td></td>
                                        <td>{sub_index === 0 ? resolveJournalName(item.id_booking_journal) : ""}</td>
                                        <td>{sub_index === 0 ? (() => { const d = entryDocument(item, lang); if (d.text) { return d.to ? <Link to={d.to} className="text-primary fw-bold" onClick={(e) => e.stopPropagation()}>{d.text}</Link> : <span>{d.text}</span> } return <span>{sub_item.description}</span> })() : sub_item.description}</td>
                                        <td className="text-end text-nowrap">{formatMoney(sub_item.amount)}</td>
                                        <td className="text-center">{sub_index === 0 && <ButtonEntryDocuments vouchers={item.vouchers} />}</td>
                                      </tr>
                                    )}))}
                                    { item.credits.map (( (sub_item, sub_index) => { return (
                                      <tr key={sub_index} className="row-dashed">
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td><div>{accountingAccountCode(accountingAccountsForCompany, sub_item.id_account)}</div><div className="text-muted fs-8">{accountingAccountName(accountingAccountsForCompany, companyForUser.language, sub_item.id_account)}</div></td>
                                        <td></td>
                                        <td>{sub_item.description}</td>
                                        <td className="text-end text-nowrap">{formatMoney(sub_item.amount)}</td>
                                        <td></td>
                                      </tr>
                                    )}))}
                                  </>
                                }
                              </tbody>
                            )}))}
                          </>
                          :
                          <tbody>
                            <tr className="text-secondary fw-bold">
                              <td colSpan={8} className="text-center">{lang.labels.noRecordsFound}</td>
                            </tr>
                          </tbody>
                        }
                      </>
                      :
                      <tbody>
                        <tr>
                          <td colSpan={8}>
                            <AppLoading></AppLoading>
                          </td>
                        </tr>
                      </tbody>
                    }
                  </table>
                </div>
                <AppPagination showOption={false} pagination={pagination} loadPagination={loadPagination}></AppPagination>
              </div>
            </div>
          </div>
          <div className="card mt-auto bd-highlight">
            <div className="card-body">
              <div className="row">
                <div className="col-auto mb-5 me-10">
                  <div className="d-flex align-items-center position-relative h-100">
                    <div className="position-absolute top-0 start-0 rounded h-100 bg-secondary w-5px"></div>
                    <div className="d-flex flex-column ms-5">
                      <label className="fw-bold mb-1">{lang.labels.bookingJournal}</label>
                      <Select inputId="input-journal" menuPlacement="bottom" menuPosition="fixed" isSearchable={false} classNames={CustomSelectClassNames} styles={CustomSelectStyles} components={{DropdownIndicator: null, IndicatorSeparator: null}} placeholder={lang.labels.bookingJournal.toUpperCase()} isDisabled={!!bookingJournal || filter.booking_journal.length === 0} options={options.journal} value={entryJournal.valid ? {value: entryJournal.value, label: resolveJournalName(entryJournal.value)} : null} onChange={handleChangeJournal} />
                      <AppLegend attribute={{check: entryJournal.valid, name: "journal", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend>
                    </div>
                  </div>
                </div>
                <div className="col-auto mb-5 me-10">
                  <div className="d-flex align-items-center position-relative h-100">
                    <div className="position-absolute top-0 start-0 rounded h-100 bg-secondary w-5px"></div>
                    <div className="d-flex align-items-center mx-5">
                      <div className="form-check form-switch form-check-custom form-check-solid">
                        <input className="form-check-input h-25px w-45px" type="checkbox" checked={type === "multiple"} onChange={handleChangeType} disabled={treasuryLockedRows.size > 0} />
                      </div>
                      <div className="d-flex flex-column text-nowrap ms-3">
                        <label className="fw-bold">{lang.labels.multipleWriting}</label>
                      </div>
                    </div>
                  </div>
                </div>
                { type !== "multiple" &&
                <div className="col mb-5">
                  <div className="position-relative">
                    <div className="position-absolute top-0 start-0 rounded h-100 bg-secondary w-5px"></div>
                      <label className="fw-bold mb-1 ms-5">{lang.labels.vatOptions}</label>
                      <div className="d-flex align-items-center">
                        <div className="mb-0 ms-5">
                          <Dropdown>
                            <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 select2-container--below select2-container--focus select2-container--open w-250px p-0">
                              <span className="selection">
                                <span className="select2-selection select2-selection--single form-select">
                                  <span className="select2-selection__rendered" role="textbox">
                                    {taxCode.length ? taxFeeSelected(settingsForCompany?.tax_fees ?? [], taxCode, companyForUser?.language ?? 'fr') : lang.labels.taxFee}
                                  </span>
                                </span>
                              </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100" align="end">
                              <span className="select2-dropdown select2-dropdown--below">
                                <span className="select2-results">
                                  <ul className="select2-results__options" role="listbox">
                                    <li className="select2-results__option select2-results__option--selectable" role="option" aria-selected={false}>
                                      <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeTaxFee("", 0)}>{lang.labels.none}</Dropdown.Item>
                                    </li>
                                    { (settingsForCompany?.tax_fees ?? []).filter(item => item.active).map (( (item, index) => { return (
                                      <li key={index} className={`select2-results__option select2-results__option--selectable ${item.code === taxCode && "select2-results__option--selected"}`} role="option" aria-selected={item.code === taxCode}>
                                        <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeTaxFee(item.code, item.rate)}>{item.label[(companyForUser.language ?? 'fr').toLowerCase() as keyof Names]}</Dropdown.Item>
                                      </li>
                                    )}))}
                                  </ul>
                                </span>
                              </span>
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                        <div className="mb-0 ms-5">
                          <label className="d-flex cursor-pointer mb-1">
                            <span className="form-check form-check-custom form-check-solid">
                              <input className="form-check-input h-20px w-20px" type="radio" value="false" checked={taxIncluded === "false"} onClick={handleChangeTaxIncluded} readOnly />
                            </span>
                            <span className="text-nowrap ms-2">
                              {lang.labels.net} ({lang.labels.taxExcluded})
                            </span>
                          </label>
                          <label className="d-flex cursor-pointer mb-0">
                            <span className="form-check form-check-custom form-check-solid">
                              <input className="form-check-input h-20px w-20px" type="radio" value="true" checked={taxIncluded === "true"} onClick={handleChangeTaxIncluded} readOnly />
                            </span>
                            <span className="text-nowrap ms-2">
                              {lang.labels.gross} ({lang.labels.taxIncluded})
                            </span>
                          </label>
                        </div>
                        <div className="mb-0 ms-5">
                          <label className="d-flex cursor-pointer mb-1">
                            <span className="form-check form-check-custom form-check-solid">
                              <input className="form-check-input h-20px w-20px" type="radio" value="debit" checked={transaction === "debit"} onClick={handleChangeTransaction} readOnly />
                            </span>
                            <span className="text-nowrap ms-2">
                              {lang.labels.debit} ({lang.labels.purchase})
                            </span>
                          </label>
                          <label className="d-flex cursor-pointer mb-0">
                            <span className="form-check form-check-custom form-check-solid">
                              <input className="form-check-input h-20px w-20px" type="radio" value="credit" checked={transaction === "credit"} onClick={handleChangeTransaction} readOnly />
                            </span>
                            <span className="text-nowrap ms-2">
                              {lang.labels.credit} ({lang.labels.sale})
                            </span>
                          </label>
                        </div>
                        { type === "multiple-simplified" && transaction === "debit" && settingsForCompany?.tax_method === "effective" &&
                          <div className="mb-0 ms-5">
                            <label className="d-flex cursor-pointer mb-1">
                              <span className="form-check form-check-custom form-check-solid">
                                <input className="form-check-input h-20px w-20px" type="radio" value="goods" checked={vatInputType === "goods"} onChange={() => setVatInputType('goods')} />
                              </span>
                              <span className="text-nowrap ms-2">
                                {lang.labels.vatInputGoods}
                              </span>
                            </label>
                            <label className="d-flex cursor-pointer mb-0">
                              <span className="form-check form-check-custom form-check-solid">
                                <input className="form-check-input h-20px w-20px" type="radio" value="investment" checked={vatInputType === "investment"} onChange={() => setVatInputType('investment')} />
                              </span>
                              <span className="text-nowrap ms-2">
                                {lang.labels.vatInputInvestment}
                              </span>
                            </label>
                          </div>
                        }
                      </div>
                      <div className={`fv-plugins-message-container invalid-feedback ${type === "multiple-simplified" && (taxCode.length === 0 || taxIncluded.length === 0 || transaction.length === 0) ? "ms-5" : "d-none"}`}>
                        <div data-field="taxt-fee" data-validator="notEmpty">{lang.labels.allValuesRequired}</div>
                      </div>
                  </div>
                </div>
                }
              </div>
              <div className="dataTables_wrapper">
                <div className="table-responsive">
                  <table className="table align-middle g-1 mb-2">
                    <colgroup>
                      <col style={{width: '150px'}} />
                      <col style={{width: '240px'}} />
                      <col style={{width: '240px'}} />
                      <col />
                      <col style={{width: '140px'}} />
                      {type === 'multiple' && <col style={{width: '220px'}} />}
                    </colgroup>
                    <thead>
                      <tr className="text-muted text-uppercase fw-bolder fs-8">
                        <th>{lang.labels.date}</th>
                        <th>{lang.labels.debit}</th>
                        <th>{lang.labels.credit}</th>
                        <th>{lang.labels.label}</th>
                        <th className="text-end">{lang.labels.sum}</th>
                        {type === 'multiple' && <th>{lang.labels.taxFee}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      { entryRows.value.map (( (item, index) => { return (
                        <React.Fragment key={index}>
                        <tr>
                          <td style={entryTdStyle}>
                            { index === 0 &&
                              <>
                                <input id="input-date" className="form-control" type="date" autoComplete="off" name="date" max={currentDate()} value={entryDate.value.substring(0, 10)} onChange={handleChangeDate} onKeyDown={(event) => focusEnter(`input-debit${suffixLegend(index)}`, true, event)} />
                                <div style={legendAbsStyle}><AppLegend attribute={{check: entryDate.valid, name: "date", index: null, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend></div>
                              </>
                            }
                          </td>
                          <td style={entryTdStyle}>
                            <Select inputId={`input-debit${suffixLegend(index)}`} menuPlacement="top" menuPosition="fixed" classNames={CustomSelectClassNames} styles={accountSelectStyles} components={{DropdownIndicator: null, IndicatorSeparator: null}} placeholder={lang.labels.debit.toUpperCase()} isDisabled={(type === "multiple" && item.credit.id.length > 0) || treasuryLockedRows.has(index)} options={options.debit} value={entryRows.valid[index].debit && entryRows.value[index].debit.id.length > 0 ? {value: item.debit.id, label: item.debit.text} : null} onChange={(option) => handleChangeDebit(index, option)} />
                            <div style={legendAbsStyle}><AppLegend attribute={{check: entryRows.valid[index].debit, name: "debit", index: index, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend></div>
                          </td>
                          <td style={entryTdStyle}>
                            <Select inputId={`input-credit${suffixLegend(index)}`} menuPlacement="top" menuPosition="fixed" classNames={CustomSelectClassNames} styles={accountSelectStyles} components={{DropdownIndicator: null, IndicatorSeparator: null}} placeholder={lang.labels.credit.toUpperCase()} isDisabled={(type === "multiple" && item.debit.id.length > 0) || treasuryLockedRows.has(index)} options={options.credit} value={entryRows.valid[index].credit && entryRows.value[index].credit.id.length > 0 ? {value: item.credit.id, label: item.credit.text} : null} onChange={(option) => handleChangeCredit(index, option)} />
                            <div style={legendAbsStyle}><AppLegend attribute={{check: entryRows.valid[index].credit, name: "credit", index: index, sub_index: null}} container={{valid: false, required: true, identical: false, exists: false}}></AppLegend></div>
                          </td>
                          <td style={entryTdStyle}>
                            <input id={`input-label${suffixLegend(index)}`} className="form-control" type="text" autoComplete="off" name="label" placeholder={lang.labels.label.toUpperCase()} value={item.label} onChange={(event) => handleChangeLabel(index, event)} onKeyUp={() => validateLabel(index)} onBlur={() => validateLabel(index)} onKeyDown={(event) => focusEnter(`input-sum${suffixLegend(index)}`, false, event)} />
                            <div style={legendAbsStyle}><AppLegend attribute={{check: entryRows.valid[index].label, name: "label", index: index, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend></div>
                          </td>
                          <td style={entryTdStyle}>
                            <input id={`input-sum${suffixLegend(index)}`} className={`form-control${treasuryLockedRows.has(index) ? ' bg-secondary text-muted' : ''}${rowCurrency[index]?.code ? ' bg-light-primary' : ''}`} type="text" autoComplete="off" name="sum" placeholder={`${lang.labels.sum.toUpperCase()}${rowCurrency[index]?.code ? ' (CHF)' : ''}`} value={item.sum} readOnly={treasuryLockedRows.has(index) || !!rowCurrency[index]?.code} onChange={(event) => handleChangeSum(index, event)} onKeyUp={() => validateSum(index)} onBlur={() => validateSum(index)} onKeyDown={(event) => executeEnterSum(index, event)} />
                            <div style={legendAbsStyle}><AppLegend attribute={{check: entryRows.valid[index].sum, name: "sum", index: index, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend></div>
                          </td>
                          { type === 'multiple' &&
                            <td style={entryTdStyle}>
                              <Dropdown>
                                <Dropdown.Toggle variant="select2 select2-container select2-container--bootstrap5 p-0" disabled={treasuryLockedRows.has(index)}>
                                  <span className="selection">
                                    <span className="select2-selection select2-selection--single form-select">
                                      <span className="select2-selection__rendered" role="textbox">
                                        {(rowTaxCodes[index] ?? '').length > 0 ? taxFeeSelected(settingsForCompany?.tax_fees ?? [], rowTaxCodes[index], companyForUser?.language ?? 'fr') : lang.labels.taxFee}
                                      </span>
                                    </span>
                                  </span>
                                </Dropdown.Toggle>
                                <Dropdown.Menu variant="select2-container select2-container--bootstrap5 select2-container--open w-100">
                                  <span className="select2-dropdown select2-dropdown--below">
                                    <span className="select2-results">
                                      <ul className="select2-results__options" role="listbox">
                                        <li className="select2-results__option select2-results__option--selectable" role="option" aria-selected={false}>
                                          <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeRowTaxCode(index, '')}>{lang.labels.none}</Dropdown.Item>
                                        </li>
                                        { (settingsForCompany?.tax_fees ?? []).filter(tf => tf.active).map((tf, ti) => (
                                          <li key={ti} className={`select2-results__option select2-results__option--selectable ${tf.code === rowTaxCodes[index] && "select2-results__option--selected"}`} role="option" aria-selected={tf.code === rowTaxCodes[index]}>
                                            <Dropdown.Item bsPrefix="select2-results__option__text" onClick={() => handleChangeRowTaxCode(index, tf.code)}>{tf.label[(companyForUser?.language ?? 'fr').toLowerCase() as keyof Names]}</Dropdown.Item>
                                          </li>
                                        ))}
                                      </ul>
                                    </span>
                                  </span>
                                </Dropdown.Menu>
                              </Dropdown>
                            </td>
                          }
                        </tr>
                        { rowCurrency[index]?.code &&
                          <tr>
                            <td></td>
                            <td colSpan={2}>
                              <div className="d-flex flex-column gap-1">
                                <div className="d-flex align-items-center gap-2">
                                  <span className="badge badge-light-primary">{rowCurrency[index].code}</span>
                                  <small className="text-muted">{lang.labels.exchangeRate}: 1 CHF =</small>
                                  <input className="form-control form-control-sm" style={{width: '90px'}} type="text" value={rowCurrency[index].exchange_rate} onChange={(e) => handleChangeExchangeRate(index, e.target.value)} placeholder={lang.labels.exchangeRate} />
                                  <small className="text-muted">{rowCurrency[index].code}</small>
                                </div>
                                { rowCurrency[index].exchange_rate && parseFloat(rowCurrency[index].exchange_rate) > 0 &&
                                  <small className="text-muted ms-4" style={{fontSize: '0.75rem'}}>
                                    {lang.labels.rateInfo}: 1 {rowCurrency[index].code} ≈ {(1 / parseFloat(rowCurrency[index].exchange_rate)).toFixed(6)} CHF
                                  </small>
                                }
                              </div>
                            </td>
                            <td>
                              <small className="text-muted">{lang.labels.amountInCurrency.replace('{currency}', rowCurrency[index].code)}</small>
                            </td>
                            <td>
                              <input className="form-control form-control-sm" type="text" value={rowCurrency[index].amount_original} onChange={(e) => handleChangeAmountOriginal(index, e.target.value)} placeholder={rowCurrency[index].code} />
                            </td>
                            {type === 'multiple' && <td></td>}
                          </tr>
                        }
                        </React.Fragment>
                      )}))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-sm btn-light-primary position-relative" disabled={entry.valid ? voucherList.length === 0 : vouchers.value.length === 0 && pendingAttachments.length === 0} onClick={() => modalShow('modal-view-vouchers')}>
                  <span className="svg-icon svg-icon-3 me-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M17.5 11H6.5C4 11 2 9 2 6.5C2 4 4 2 6.5 2H17.5C20 2 22 4 22 6.5C22 9 20 11 17.5 11ZM15 6.5C15 7.9 16.1 9 17.5 9C18.9 9 20 7.9 20 6.5C20 5.1 18.9 4 17.5 4C16.1 4 15 5.1 15 6.5Z" fill="black"/>
                      <path opacity="0.3" d="M17.5 22H6.5C4 22 2 20 2 17.5C2 15 4 13 6.5 13H17.5C20 13 22 15 22 17.5C22 20 20 22 17.5 22ZM4 17.5C4 18.9 5.1 20 6.5 20C7.9 20 9 18.9 9 17.5C9 16.1 7.9 15 6.5 15C5.1 15 4 16.1 4 17.5Z" fill="black"/>
                    </svg>
                  </span>
                  {lang.labels.viewVouchers}
                  { (() => {
                    const count = entry.valid ? voucherList.length : vouchers.value.length + pendingAttachments.length
                    return count > 0 ? <span className="badge badge-circle badge-sm badge-primary ms-2">{count}</span> : null
                  })() }
                </button>
                <button type="button" className="btn btn-sm btn-light-primary" onClick={() => modalShow('modal-voucher-upload-file')}>
                  <span className="svg-icon svg-icon-3 me-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M19 15C19 17.2 17.2 19 15 19H5C2.8 19 1 17.2 1 15C1 13.2 2.2 11.6 4 11.1C3.4 10.4 3 9.5 3 8.5C3 6.6 4.6 5 6.5 5C7.1 5 7.7 5.1 8.2 5.4C9 3.4 11 2 13.5 2C16.5 2 19 4.5 19 7.5C19 8 18.9 8.5 18.8 9C20.6 9.5 22 11.1 22 13C22 15.2 20.2 17 18 17H17" fill="black"/>
                      <path d="M12 8L7 13H10V18H14V13H17L12 8Z" fill="black"/>
                    </svg>
                  </span>
                  {lang.labels.uploadNewPdf}
                </button>
                <button type="button" className="btn btn-sm btn-light-primary" onClick={() => modalShow('modal-attach-document')}>
                  <span className="svg-icon svg-icon-3 me-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22ZM16 13H13V10C13 9.4 12.6 9 12 9C11.4 9 11 9.4 11 10V13H8C7.4 13 7 13.4 7 14C7 14.6 7.4 15 8 15H11V18C11 18.6 11.4 19 12 19C12.6 19 13 18.6 13 18V15H16C16.6 15 17 14.6 17 14C17 13.4 16.6 13 16 13Z" fill="black" />
                      <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                    </svg>
                  </span>
                  {lang.labels.attachExistingDocument}
                </button>
              </div>
              { !entry.valid && (vouchers.value.length > 0 || pendingAttachments.length > 0) &&
                <div className="d-flex flex-wrap gap-2 mt-2">
                  { vouchers.value.map((v, i) => (
                    <span key={`v-${i}`} className="badge badge-light-primary d-flex align-items-center gap-1 py-2 px-3">
                      <i className="bi bi-file-earmark-pdf fs-7"></i>
                      <span className="fs-8">{v.name}</span>
                      <i className="bi bi-x fs-5 cursor-pointer" onClick={() => setVouchers(prev => ({...prev, value: prev.value.filter((_, idx) => idx !== i), valid: prev.valid.filter((_, idx) => idx !== i)}))}></i>
                    </span>
                  ))}
                  { pendingAttachments.map((att, i) => (
                    <span key={`a-${i}`} className="badge badge-light-success d-flex align-items-center gap-1 py-2 px-3">
                      <i className="bi bi-paperclip fs-7"></i>
                      <span className="fs-8">{att.name}</span>
                      <i className="bi bi-x fs-5 cursor-pointer" onClick={() => setPendingAttachments(prev => prev.filter((_, idx) => idx !== i))}></i>
                    </span>
                  ))}
                </div>
              }
            </div>
            { companyForUser &&
              <>
                <AppModalVoucherUploadFile
                  setAsideMinimize={() => {}}
                  record={selectedEntry ?? {}}
                  vouchers={vouchers}
                  setVouchers={setVouchers}
                />
                <AppModalAttachDocument
                  id_company={companyForUser.id}
                  entity_type="accounting-entry"
                  id_entity={entry.valid ? entry.value : undefined}
                  onAttached={handleVouchersChanged}
                  onPick={!entry.valid ? (idDoc, doc) => setPendingAttachments(prev => [...prev, {id: idDoc, name: doc?.name ?? idDoc}]) : undefined}
                />
                <div id="modal-view-vouchers" className="modal fade" tabIndex={-1} aria-hidden="true">
                  <div className="modal-dialog modal-dialog-centered mw-700px">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h2 className="modal-title">{lang.labels.vouchers}</h2>
                        <div className="btn btn-sm btn-icon btn-active-color-primary" data-bs-dismiss="modal">
                          <span className="svg-icon svg-icon-1">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                              <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                            </svg>
                          </span>
                        </div>
                      </div>
                      <div className="modal-body py-5 px-lg-10">
                        { entry.valid
                          ?
                          voucherList.length > 0
                          ?
                          <div className="table-responsive">
                            <table className="table align-middle table-row-dashed gy-2 mb-0">
                              <thead className="border-bottom">
                                <tr className="text-muted text-uppercase fw-bolder fs-7">
                                  <th className="min-w-200px text-start">{lang.labels.name}</th>
                                  <th className="w-75px text-end">{lang.labels.download}</th>
                                  { !selectedEntry?.automated &&
                                    <th className="w-50px text-end"></th>
                                  }
                                </tr>
                              </thead>
                              <tbody className="text-secondary fw-bold">
                                { voucherList.map((item: Voucher, index: number) => (
                                  <tr key={index}>
                                    <td>
                                      <span className="cursor-pointer fw-bolder text-hover-primary" onClick={() => executeOpenVoucher(item)}>
                                        {item.original_name ?? item.name ?? 'Attachment'}
                                      </span>
                                    </td>
                                    <td className="text-end">
                                      <button className="btn btn-sm btn-icon btn-bg-light btn-active-color-success h-30px w-30px" type="button" onClick={() => executeDownloadVoucher(item)}>
                                        <span className="svg-icon svg-icon-primary svg-icon-3">
                                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path opacity="0.3" d="M19 15C19 17.2 17.2 19 15 19H5C2.8 19 1 17.2 1 15C1 13.2 2.2 11.6 4 11.1C3.4 10.4 3 9.5 3 8.5C3 6.6 4.6 5 6.5 5C7.1 5 7.7 5.1 8.2 5.4C9 3.4 11 2 13.5 2C16.5 2 19 4.5 19 7.5C19 8 18.9 8.5 18.8 9C20.6 9.5 22 11.1 22 13C22 15.2 20.2 17 18 17H17" fill="black"/>
                                            <path d="M12 16L7 11H10V6H14V11H17L12 16Z" fill="black"/>
                                          </svg>
                                        </span>
                                      </button>
                                    </td>
                                    { !selectedEntry?.automated &&
                                      <td className="text-end">
                                        <button className="btn btn-sm btn-icon btn-bg-light btn-active-color-danger h-30px w-30px" type="button" onClick={() => executeDeleteVoucher(index)}>
                                          <span className="svg-icon svg-icon-danger svg-icon-3">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                              <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black" />
                                              <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black" />
                                              <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black" />
                                            </svg>
                                          </span>
                                        </button>
                                      </td>
                                    }
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          :
                          <div className="text-center text-muted py-5">{lang.labels.noRecordsFound}</div>
                          :
                          <div>
                            { vouchers.value.length > 0 &&
                              <div className="mb-3">
                                <h6 className="fw-bold mb-2">{lang.labels.uploadNewPdf}</h6>
                                { vouchers.value.map((v, i) => (
                                  <div key={i} className="d-flex align-items-center justify-content-between py-1">
                                    <div className="d-flex align-items-center">
                                      <span className="svg-icon svg-icon-2 me-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                          <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="black" />
                                          <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                                        </svg>
                                      </span>
                                      <span>{v.name}</span>
                                    </div>
                                    <button className="btn btn-sm btn-icon btn-bg-light btn-active-color-danger h-25px w-25px" type="button" onClick={() => setVouchers(prev => ({...prev, value: prev.value.filter((_, idx) => idx !== i), valid: prev.valid.filter((_, idx) => idx !== i)}))}>
                                      <i className="bi bi-x fs-4"></i>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            }
                            { pendingAttachments.length > 0 &&
                              <div className="mb-3">
                                <h6 className="fw-bold mb-2">{lang.labels.attachExistingDocument}</h6>
                                { pendingAttachments.map((att, i) => (
                                  <div key={i} className="d-flex align-items-center justify-content-between py-1">
                                    <div className="d-flex align-items-center">
                                      <span className="svg-icon svg-icon-2 me-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                          <path opacity="0.3" d="M19 22H5C4.4 22 4 21.6 4 21V3C4 2.4 4.4 2 5 2H14L20 8V21C20 21.6 19.6 22 19 22Z" fill="black" />
                                          <path d="M15 8H20L14 2V7C14 7.6 14.4 8 15 8Z" fill="black" />
                                        </svg>
                                      </span>
                                      <span>{att.name}</span>
                                    </div>
                                    <button className="btn btn-sm btn-icon btn-bg-light btn-active-color-danger h-25px w-25px" type="button" onClick={() => setPendingAttachments(prev => prev.filter((_, idx) => idx !== i))}>
                                      <i className="bi bi-x fs-4"></i>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            }
                            { vouchers.value.length === 0 && pendingAttachments.length === 0 &&
                              <div className="text-center text-muted py-5">{lang.labels.noRecordsFound}</div>
                            }
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </>
            }
            <div className="card-footer d-flex justify-content-end">
              { entry.valid
                ?
                <>
                  <button className="btn btn-lg btn-light fw-bolder text-uppercase me-2" type="reset" onClick={uploadData}>
                    {lang.labels.cancel}
                  </button>
                  <button className="btn btn-lg btn-danger fw-bolder text-uppercase me-2" type="button" data-kt-indicator={loadIndicator} onClick={submitAccountingEntryDelete}>
                    <span className="indicator-label text-uppercase text-nowrap">{lang.labels.delete}</span>
                    <span className="indicator-progress">
                      {lang.labels.pleaseWait}
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  </button>
                  <button className="btn btn-xl btn-primary fw-bolder" type="button" data-kt-indicator={loadIndicator} onClick={submitAccountingEntryUpdate}>
                    <span className="indicator-label text-uppercase text-nowrap">{lang.labels.update}</span>
                    <span className="indicator-progress">
                      {lang.labels.pleaseWait}
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  </button>
                </>
                :
                <button className="btn btn-xl btn-primary fw-bolder" type="button" data-kt-indicator={loadIndicator} onClick={submitAccountingEntryCreate}>
                  <span className="indicator-label text-uppercase text-nowrap">{lang.labels.save}</span>
                  <span className="indicator-progress">
                    {lang.labels.pleaseWait}
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                </button>
              }
            </div>
          </div>
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default JournalViewPage;
